import { useContext, useMemo } from "react";
import { RebalanceContext } from "../../RebalanceInfo";
import { useQueryModels } from "../../../models/lib";
import type { AccountRecord } from "../lib/explainTypes";

// The v3 explain payload is intentionally PII-free (`api/src/.../explain_v2.py`
// AccountInfo docstring: account_name and account_number are excluded so
// explain rows can be fed to external LLM check agents). The UI joins
// name/number back in here from `RebalanceContext.household.accounts`,
// which the parent route already loads. Numbers are masked client-side
// before display.
//
// model.name and rolled-up balance are NOT a PII rule -- those are separate
// Python emitter follow-ups (transcoder TODOs in explain_v2_to_v3.py:329 and
// upstream HouseholdRebalanceAccountRequest.balance population). Until those
// land we backfill from the same household snapshot + cached model list.
export interface ResolvedAccountInfo {
  name: string;
  number: string | null;
  maskedNumber: string | null;
  balance: number | null;
  modelId: number | null;
  modelName: string | null;
  modelSource: AccountRecord["model_source"];
}

// Keep the last 3 alphanumeric characters, X-mask everything else to a
// fixed 5-char prefix (e.g. "IRA-SW001" -> "XXXXX001"). Matches the
// pattern used by legacy explain v1 for client-facing account numbers.
export function maskAccountNumber(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const stripped = raw.replace(/[^A-Za-z0-9]/g, "");
  if (stripped.length === 0) return null;
  const tail = stripped.slice(-3);
  return "XXXXX" + tail;
}

export function useAccountResolver(): (
  account: AccountRecord,
) => ResolvedAccountInfo {
  const { household, givenCheck } = useContext(RebalanceContext);
  // A given check is the admin Explain viewer's (R12): the model list is the
  // staff user's own firm, so it is not read and an unnamed model stays unnamed.
  const { data: models } = useQueryModels({
    enabled: givenCheck === undefined,
  });

  return useMemo(() => {
    const accountById = new Map(
      (household?.accounts ?? []).map((a) => [a.id, a]),
    );
    const modelNameById = new Map(
      (models ?? []).map((m) => [m.id, m.name] as const),
    );

    return (account: AccountRecord) => {
      const hh = accountById.get(account.account_id);
      // An account with no model of its own carries the household model on
      // `account.model` for the allocation math, but must NOT be labelled
      // with that model in the explain UI -- it has no assigned model.
      // `model_source` is the authoritative signal: completion /
      // soft-completion set it to "residual" for accounts that absorb
      // leftover allocation, and "none" means no model exists at any level.
      // In both cases `account.model.id`/`.name` still point at the
      // inherited household model, so we suppress them here and let
      // consumers render "No Model Assigned" (APP-1346).
      const hasAssignedModel =
        account.model_source === "account" ||
        account.model_source === "household";
      const modelId = hasAssignedModel ? account.model.id : null;
      // Cross-firm admin path supplies a non-PII `label` (and NO name/number);
      // advisors supply `displayName`/`displayNumber` as before. Prefer the
      // label when present so the admin Explain tree shows the redacted token
      // and renders no client name or number
      // (s-admin-view-pii-minimization-019eb293 R5/R6/AC5).
      const label = (hh as { label?: string } | undefined)?.label;
      const rawNumber = label != null ? null : (hh?.displayNumber ?? null);
      return {
        name: label ?? hh?.displayName ?? `Account ${account.account_id}`,
        number: rawNumber,
        maskedNumber: maskAccountNumber(rawNumber),
        balance: hh?.accountBalance ?? null,
        modelId,
        modelName: hasAssignedModel
          ? (account.model.name ??
            (modelId != null ? (modelNameById.get(modelId) ?? null) : null))
          : null,
        modelSource: account.model_source,
      };
    };
  }, [household, models]);
}

export interface ResolvedHouseholdInfo {
  id: number | undefined;
  name: string | null;
}

export function useHouseholdResolver(): ResolvedHouseholdInfo {
  const { household } = useContext(RebalanceContext);
  // Cross-firm admin path supplies a non-PII `label` (`Household <id>`) and NO
  // client household name; advisors supply `name`. Prefer the label so the
  // admin Explain header renders no client household name
  // (s-admin-view-pii-minimization-019eb293 R5/AC4).
  const label = (household as { label?: string } | undefined)?.label;
  return {
    id: household?.id,
    name: label ?? household?.name ?? null,
  };
}
